import { StanInput, StanModal } from '@/components';
import { useBalance } from '@/queries/useBalance';
import { useUser } from '@/queries/useUser';
import { MINIMUM_ALLOWED_BALANCE, SENTRY_FEATURES, stanAnalytics, stanNotify } from '@/lib';
import { partialRefundOrder } from '@/lib/api/income/api';
import { formatCurrency } from '@/lib/currency';
import { useAuthStore } from '@/stores';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AdminPartialRefundModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId?: string;
    /** Upper bound for partial refund (creator revenue), same as Vue AdminCard `revenue` */
    maxRefundAmount: number;
    currency: string;
    onRefunded: () => void;
}

export function AdminPartialRefundModal({
    isOpen,
    onClose,
    orderId,
    maxRefundAmount,
    currency,
    onRefunded,
}: AdminPartialRefundModalProps) {
    const { balance, refetchBalance } = useBalance();
    const { user } = useUser();
    const token = useAuthStore(state => state.token);
    const [partialRefundAmount, setPartialRefundAmount] = useState(0);
    const [loading, setLoading] = useState(false);

    const maxAmountFormatted = useMemo(() => formatCurrency(maxRefundAmount, currency), [maxRefundAmount, currency]);

    const priceValidationErrorMessage = useMemo(() => {
        if (partialRefundAmount > maxRefundAmount) {
            return `Must be less than ${maxAmountFormatted}`;
        }
        if (partialRefundAmount <= 0) {
            return 'Must be greater than 0';
        }
        return null;
    }, [partialRefundAmount, maxRefundAmount, maxAmountFormatted]);

    const netBalanceAfterRefund =
        (balance?.stripe_available?.amount ?? 0) + (balance?.stripe_pending?.amount ?? 0) - partialRefundAmount;

    const insufficientBalanceForRefund = netBalanceAfterRefund < MINIMUM_ALLOWED_BALANCE;

    const header = insufficientBalanceForRefund ? 'Insufficient Funds Available' : 'Process a Partial Refund';

    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) return;
        wasOpenRef.current = true;

        setPartialRefundAmount(0);
        void refetchBalance();

        const stripeTotal = (balance?.stripe_available?.amount ?? 0) + (balance?.stripe_pending?.amount ?? 0);
        const insufficientAtOpen = stripeTotal < MINIMUM_ALLOWED_BALANCE;

        stanAnalytics('admin-partial-refund-modal-opened', {
            meta: { user_id: user?.user_id, username: user?.username },
            props: {
                payment_id: orderId,
                insufficient_balance: insufficientAtOpen,
                amount: 0,
            },
        });
    }, [
        isOpen,
        orderId,
        user?.user_id,
        user?.username,
        refetchBalance,
        balance?.stripe_available?.amount,
        balance?.stripe_pending?.amount,
    ]);

    const handlePrimary = () => {
        if (priceValidationErrorMessage || insufficientBalanceForRefund) return;
        setLoading(true);
        partialRefundOrder(orderId, partialRefundAmount, token, user?.user_id)
            .then(() => {
                stanNotify({
                    type: 'success',
                    title: 'Transaction Refund',
                    text: 'This transaction has been successfully refunded.',
                    duration: 2000,
                });
                onRefunded();
                void refetchBalance();
            })
            .catch((error: any) => {
                stanNotify({
                    type: 'error',
                    title: 'Transaction Refund',
                    text: error?.response?.data?.message || 'Something went wrong. Please contact our support.',
                    duration: 5000,
                    error: {
                        message: '[INCOME] failed-to-partial-refund',
                        options: {
                            tags: { feature: SENTRY_FEATURES.INCOME },
                            context: { error },
                        },
                    },
                });
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    };

    return (
        <StanModal
            id='admin-partial-refund-modal'
            isOpen={isOpen}
            onClose={onClose}
            title={header}
            primaryActionLabel='Submit'
            primaryActionLoading={loading}
            primaryActionDisabled={
                loading || insufficientBalanceForRefund || !!priceValidationErrorMessage || !orderId || !user?.user_id
            }
            onPrimaryAction={handlePrimary}
        >
            {insufficientBalanceForRefund && (
                <div className='flex flex-col gap-4'>
                    <div className='text-gray flex w-full content-between items-center'>
                        <span className='text-gray text-sm'>
                            Uh oh! It looks like your account balance is too low to process this refund.
                        </span>
                    </div>
                </div>
            )}
            <div className='mt-0 flex flex-col gap-4 sm:mt-6'>
                <h3 className='font-semibold'>Maximum Refund Amount: {maxAmountFormatted}</h3>
                <div>
                    <StanInput
                        name='admin-partial-refund-amount'
                        type='number'
                        step='any'
                        min={0}
                        topLabel='Partial Refund Amount'
                        value={String(partialRefundAmount)}
                        onChange={e => setPartialRefundAmount(parseFloat(e.target.value) || 0)}
                        error={!!priceValidationErrorMessage}
                        hintText={priceValidationErrorMessage ?? undefined}
                    />
                </div>
            </div>
        </StanModal>
    );
}
